import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PostListQuery } from '@bb/shared';
import { Panel } from '@/components/ui/Panel';
import { Select } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { count } from '@/lib/format';
import { useTargets } from '@/features/targets/useTargets';
import { EmergencyBanner } from './EmergencyBanner';
import { PostList } from './PostList';
import { usePostActions, usePostStream, type FeedFilters } from './usePosts';

type ReadFilter = 'all' | 'unread' | 'pinned';

export function FeedPage() {
  const targets = useTargets();
  const [targetId, setTargetId] = useState<string>('');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');

  const filters = useMemo<FeedFilters>(
    () => ({
      ...(targetId ? { targetIds: [targetId] } : {}),
      ...(readFilter === 'unread' ? { isRead: false } : {}),
      ...(readFilter === 'pinned' ? { isPinned: true } : {}),
    }),
    [targetId, readFilter],
  );

  const params = useMemo<PostListQuery>(
    // Deleted posts leave the live feed and live on their own screen; the
    // archive still shows them so the record stays complete.
    () => ({ sort: 'newest', limit: 40, isDeleted: false, ...filters }),
    [filters],
  );

  const stream = usePostStream('feed', params, filters);
  const actions = usePostActions(stream.patchLocal);

  const activeTargets = targets.data?.filter((target) => target.isActive) ?? [];

  return (
    <Panel
      label="Live feed"
      flush
      className="h-full"
      actions={
        <>
          {stream.total !== null ? (
            <Badge tone="neutral">{count(stream.total)} archived</Badge>
          ) : null}

          <Select
            value={readFilter}
            onChange={(event) => setReadFilter(event.target.value as ReadFilter)}
            className="w-28"
            aria-label="Status filter"
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="pinned">Pinned</option>
          </Select>

          <Select
            value={targetId}
            onChange={(event) => setTargetId(event.target.value)}
            className="w-44"
            aria-label="Target filter"
          >
            <option value="">All targets</option>
            {targets.data?.map((target) => (
              <option key={target.id} value={target.id}>
                @{target.handle}
              </option>
            ))}
          </Select>
        </>
      }
      bodyClassName="flex min-h-0 flex-col"
    >
      <EmergencyBanner onAcknowledge={(post) => actions.toggleRead(post)} />

      <PostList
        items={stream.items}
        liveIds={stream.liveIds}
        isLoading={stream.query.isPending}
        error={stream.query.error}
        hasNextPage={stream.query.hasNextPage}
        isFetchingNextPage={stream.query.isFetchingNextPage}
        onLoadMore={() => void stream.query.fetchNextPage()}
        onRetry={() => void stream.query.refetch()}
        onTogglePin={actions.togglePin}
        onToggleRead={actions.toggleRead}
        emptyTitle={activeTargets.length === 0 ? 'No active targets' : 'Standing by'}
        emptyDetail={
          activeTargets.length === 0
            ? 'Nothing is being watched yet. Add an X account and posts will appear here as they are delivered.'
            : 'Targets are live. Posts appear here the moment they are delivered — no refresh needed.'
        }
        emptyAction={
          activeTargets.length === 0 ? (
            <Link to="/targets">
              <Button size="sm" variant="primary">
                Add a target
              </Button>
            </Link>
          ) : null
        }
      />
    </Panel>
  );
}
